import Button from "components/Button";
import DatePicker from "components/DatePicker";
import SelectField from "components/SelectField";
import moment from "moment";

import styled from "@emotion/styled";

import { Box, Chip, Tooltip, Typography } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import ParkkiTheme from "themes/ParkkiTheme";
import { AlertEventsActioner } from "../../../services/alertFactory/alertEvents/actioner";
import { AlertEventsUtils } from "../../../services/alertFactory/alertEvents/utils";
import { AlertPoliciesActioner } from "../../../services/alertFactory/alertPolicies/actioner";
import { AlertPoliciesUtils } from "../../../services/alertFactory/alertPolicies/utils";
import { AlertsManagement } from "./AlertsManagement/AlertsManagement";

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: ${ParkkiTheme.colors.lightGrey};
  margin-bottom: ${ParkkiTheme.spacings.xsmall};
  font-family: "Source Sans Pro", sans-serif;
  margin-bottom: -2px;
`;

const ContentContainer = styled.main`
  margin: 0 auto;
  padding: 0 ${ParkkiTheme.spacings.tiny} 0;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 16px;
  padding: 0px 24px;
  margin: 32px 0 24px 0;
  gap: 24px;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
`;

const TotalEvents = styled.div`
  display: flex;
  align-items: center;
  padding-left: 8px;

  .MuiTypography-h4 {
    color: ${ParkkiTheme.colors.primary};
    font-weight: 700;
    margin: 0;
    font-size: 1.5rem;
  }
`;

const FilterContainer = styled.div`
  flex: 1;
  min-width: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FilterContainerFixed = styled.div`
  flex: none;
  min-width: 120px;
  max-width: 160px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AlertEventCardContainer = styled.div`
  border: 1px solid ${ParkkiTheme.colors.lightGrey};
  border-radius: 16px;
  box-shadow: 0 2px 8px ${ParkkiTheme.colors.lightGrey};
  padding: 20px 24px 0px 24px;
  margin-bottom: 8px;
  background: #fff;
  position: relative;
  width: 60vw;
`;

const AlertEventHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  margin-bottom: 12px;
`;

const AlertEventIcon = styled.div`
  margin-right: 10px;
`;

const AlertEventMessage = styled.div`
  margin-bottom: ${ParkkiTheme.spacings.small};
  font-size: ${ParkkiTheme.fontSizes.regular};
  line-height: 1.5;
  color: ${ParkkiTheme.colors.darkGrey};
  padding: ${ParkkiTheme.spacings.small};
  border-radius: 8px;
`;

const AlertEventDate = styled.div`
  padding: 4px 14px;
  font-size: 0.7rem;
  color: #555;
`;

const AlertsComponent = ({
  startDate,
  endDate,
  getAlertPolicies,
  getAlertEvents,
  setFilters,
  alertPolicies,
  alertPoliciesFilters,
  alertRulesFilters,
  alertEvents,
  outputTypesFilters,
  alertEventsLimit,
}) => {
  const intl = useIntl();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [allEvents, setAllEvents] = useState([]);
  const [requestedPages, setRequestedPages] = useState(new Set());
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const total = alertEvents?.total || 0;

  useEffect(() => {
    getAlertPolicies();
    getAlertEvents();
    setAllEvents([]);
    setRequestedPages(new Set([1]));
  }, []);

  useEffect(() => {
    if (alertEvents?.data) {
      if (page === 1) {
        setAllEvents(alertEvents.data);
      } else {
        setAllEvents((prev) =>
          [...prev, ...alertEvents.data]
            .reduce((acc, event) => {
              if (!acc.some((e) => e.id === event.id)) {
                acc.push(event);
              }
              return acc;
            }, [])
            .sort((a, b) => {
              return new Date(b.created_at) - new Date(a.created_at);
            })
        );
      }

      setLoading(false);
    }
  }, [alertEvents, page]);

  const handleScroll = useCallback(() => {
    if (loading) return;

    const triggerElement = document.querySelector(
      '[data-trigger-element="true"]'
    );
    if (!triggerElement) return;

    const rect = triggerElement.getBoundingClientRect();
    const isVisible =
      rect.top <= window.innerHeight + 200 && rect.bottom >= -200;

    if (isVisible && allEvents.length < total) {
      const nextPage = page + 1;

      if (requestedPages.has(nextPage)) return;

      const expectedItemsForCurrentPage = page * alertEventsLimit;

      if (allEvents.length < expectedItemsForCurrentPage) {
        return;
      }

      setRequestedPages((prev) => new Set([...prev, nextPage]));

      setLoading(true);

      setTimeout(() => {
        getAlertEvents(nextPage);
        setPage(nextPage);
      }, 300);
    }
  }, [
    allEvents.length,
    total,
    loading,
    getAlertEvents,
    page,
    requestedPages,
    alertEventsLimit,
  ]);

  const uniqueAlertPolicies = useMemo(() => {
    return alertPolicies.filter(
      (policy, index, self) =>
        index === self.findIndex((t) => t.rule === policy.rule)
    );
  }, [alertPolicies]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  function shouldDisabledDate(date) {
    return date.isAfter(moment(), "day");
  }

  function handleDateChange(key, date) {
    if (key === "from") {
      setFilters("from", date.clone().startOf("day"));

      if (date.isAfter(moment(endDate), "day")) {
        setFilters("to", date.clone().endOf("day"));
      }
    }

    if (key === "to") {
      setFilters("to", date.clone().endOf("day"));

      if (date.isBefore(moment(startDate), "day")) {
        setFilters("from", date.clone().startOf("day"));
      }
    }
  }

  return (
    <StyledContainer>
      <FiltersContainer>
        <AlertsManagement />
        <FilterContainerFixed>
          <DatePicker
            value={startDate}
            label={intl.formatMessage({ id: "alertsScene.filters.from" })}
            onChange={(date) => {
              handleDateChange("from", date);
            }}
            shouldDisableDate={shouldDisabledDate}
          />
        </FilterContainerFixed>

        <FilterContainerFixed>
          <DatePicker
            value={endDate}
            label={intl.formatMessage({ id: "alertsScene.filters.to" })}
            onChange={(date) => {
              handleDateChange("to", date);
            }}
            shouldDisableDate={shouldDisabledDate}
          />
        </FilterContainerFixed>
        <FilterContainer>
          <SelectField
            id="alertRule"
            label={intl.formatMessage({ id: "alertsScene.filters.rule" })}
            checkbox
            values={uniqueAlertPolicies.map((policy) => {
              return {
                text: (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {AlertPoliciesUtils.getRuleIcon(policy.rule)}
                    {intl.formatMessage({
                      id: `alertsScene.rules.${policy.rule}`,
                    })}
                  </div>
                ),
                value: policy.rule,
              };
            })}
            checked={alertRulesFilters}
            onChange={(event) => {
              if (
                event?.target?.value !== undefined &&
                Array.isArray(event.target.value)
              ) {
                if (event.target.value.length > 0) {
                  setFilters("rules", event.target.value);
                } else {
                  setFilters("rules", undefined);
                }
              }
            }}
            renderValue={(selected) => {
              return selected.map((rule) => {
                return (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {AlertPoliciesUtils.getRuleIcon(rule)}
                    {intl.formatMessage({ id: `alertsScene.rules.${rule}` })}
                  </div>
                );
              });
            }}
          />
        </FilterContainer>
        <FilterContainer>
          <SelectField
            id={"alertPolicy"}
            checkbox
            label={intl.formatMessage({
              id: "alertsScene.filters.alertPolicies",
            })}
            values={
              alertPolicies?.map((policy) => {
                return {
                  text: policy.name,
                  value: policy.id,
                };
              }) ?? []
            }
            checked={alertPoliciesFilters}
            onChange={(event) => {
              if (
                event?.target?.value !== undefined &&
                Array.isArray(event.target.value)
              ) {
                if (event.target.value.length > 0) {
                  setFilters("alert_policies", event.target.value);
                } else {
                  setFilters("alert_policies", undefined);
                }
              }
            }}
          />
        </FilterContainer>
        <FilterContainer>
          <SelectField
            id={"outputTypes"}
            checkbox
            label={intl.formatMessage({
              id: "alertsScene.filters.outputTypes",
            })}
            values={AlertPoliciesUtils.getOutputTypes().map((type) => {
              return {
                value: type,
                text: intl.formatMessage({
                  id: `alertsScene.outputTypes.${type}`,
                }),
              };
            })}
            checked={outputTypesFilters}
            onChange={(event) => {
              if (
                event?.target?.value !== undefined &&
                Array.isArray(event.target.value)
              ) {
                if (event.target.value.length > 0) {
                  setFilters("output_types", event.target.value);
                } else {
                  setFilters("output_types", undefined);
                }
              }
            }}
          />
        </FilterContainer>
        <FilterContainerFixed>
          <Button
            label={intl.formatMessage({ id: "alertsScene.search" })}
            onClick={() => {
              setRequestedPages(new Set([1]));
              getAlertEvents();
            }}
            style={{
              borderRadius: 8,
              height: 48,
              width: "100%",
            }}
          />
        </FilterContainerFixed>
        {/* TODO: Add export button */}
        {/* <FilterContainerFixed>
          <Button
            label={"Exporter"}
            disabled
            style={{
              borderRadius: 8,
              height: 48,
              width: "100%",
            }}
          />
        </FilterContainerFixed> */}
      </FiltersContainer>
      <ContentContainer ref={containerRef}>
        <TitleContainer>
          {total > 0 && (
            <TotalEvents>
              <Typography variant="h4">
                {intl.formatMessage(
                  { id: "alertsScene.totalEvents" },
                  { count: total }
                )}
              </Typography>
            </TotalEvents>
          )}
        </TitleContainer>
        {allEvents && allEvents.length > 0
          ? allEvents.map((event, index) => {
              const isTriggerElement =
                allEvents.length - index <= 10 && allEvents.length - index > 0;

              return (
                <AlertEventCardContainer
                  key={event.id}
                  ref={isTriggerElement ? triggerRef : null}
                  data-trigger-element={isTriggerElement ? "true" : undefined}
                >
                  <AlertEventHeader>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <AlertEventIcon>
                        <Tooltip
                          title={intl.formatMessage({
                            id: `alertsScene.rules.${event.alert_policy_config?.rule}`,
                          })}
                        >
                          {AlertPoliciesUtils.getRuleIcon(
                            event.alert_policy_config?.rule
                          )}
                        </Tooltip>
                      </AlertEventIcon>
                      <Typography variant="caption" color="text.secondary">
                        {event.alert_policy_config?.name || null}
                      </Typography>
                    </div>
                    <AlertEventDate>
                      <Typography variant="caption" color="text.secondary">
                        {AlertEventsUtils.formatDate(event.created_at)}
                      </Typography>
                    </AlertEventDate>
                  </AlertEventHeader>
                  {event.outputs?.map((output, outputIndex) => (
                    <AlertEventMessage
                      style={{
                        ...AlertEventsUtils.getOutputColor(output),
                      }}
                      key={`${event.id}-output-${outputIndex}`}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Typography>{output.message}</Typography>
                        <Chip
                          label={intl.formatMessage({
                            id: `alertsScene.outputTypes.${output.type}`,
                          })}
                          style={{
                            color: output.text_color || "black",
                            alignSelf: "flex-end",
                          }}
                        />
                      </Box>
                    </AlertEventMessage>
                  ))}
                </AlertEventCardContainer>
              );
            })
          : null}
        {loading && allEvents.length < total && (
          <div style={{ textAlign: "center", padding: 16 }}>Chargement...</div>
        )}
      </ContentContainer>
    </StyledContainer>
  );
};

const mapStateToProps = (state) => {
  return {
    startDate: state.AlertFactory.alertEvents.filters.from,
    endDate: state.AlertFactory.alertEvents.filters.to,
    alertPoliciesFilters: state.AlertFactory.alertEvents.filters.alert_policies,
    alertRulesFilters: state.AlertFactory.alertEvents.filters.rules,
    outputTypesFilters: state.AlertFactory.alertEvents.filters.output_types,
    alertEventsLimit: state.AlertFactory.alertEvents.filters.limit,
    alertPolicies: state.AlertFactory.alertPolicies.data?.data ?? [],
    alertEvents: state.AlertFactory.alertEvents.data,
  };
};

const mapDispatchToProps = (dispatch) => ({
  setFilters: (key, value) =>
    dispatch(AlertEventsActioner.setAlertEventsFilters(key, value)),
  getAlertPolicies: () => dispatch(AlertPoliciesActioner.getAlertPolicies()),
  getAlertEvents: (page) => dispatch(AlertEventsActioner.getAlertEvents(page)),
});

export const Alerts = connect(
  mapStateToProps,
  mapDispatchToProps
)(AlertsComponent);
